import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";
import { CostModel } from "../../entities/cost";

export const costApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCostCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => `/cost/commonInfo`,
      providesTags: [{ type: TagTypes.Cost, id: "LIST" }],
    }),
    getCostFilteredPaged: builder.query<FilteredPagedQueryResult<CostModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/cost/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Cost, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.Cost,
                id,
              })),
            ]
          : [{ type: TagTypes.Cost, id: "PARTIAL-LIST" }],
    }),
    getCost: builder.query<CostModel, number>({
      query: (costId) => `/cost/getById/${costId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Cost, id: arg }],
    }),
    scrapCost: builder.query<CostModel, { tracingNumber: string }>({
      query: ({ tracingNumber }) => ({
        url: "/cost/scrap",
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.Cost, id: "Scrap" }],
    }),
    addCost: builder.mutation({
      query: (initialCost) => ({
        url: "/cost",
        method: "POST",
        body: initialCost,
      }),
      invalidatesTags: [
        { type: TagTypes.Cost, id: "LIST" },
        { type: TagTypes.Cost, id: "PARTIAL-LIST" },
      ],
    }),
    editCost: builder.mutation({
      query: (cost) => ({
        url: `/cost/${cost.id}`,
        method: "PUT",
        body: cost,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.Cost, id: arg.id }],
    }),
    deleteCost: builder.mutation({
      query: (costId) => ({
        url: `/cost/${costId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Cost, id: arg },
              { type: TagTypes.Cost, id: "LIST" },
            ],
    }),
    deleteMultiCosts: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/cost/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Cost,
                id,
              })),
              { type: TagTypes.Cost, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetCostCommonInfoQuery,
  useGetCostFilteredPagedQuery,
  useGetCostQuery,
  useAddCostMutation,
  useEditCostMutation,
  useScrapCostQuery,
  useLazyScrapCostQuery,
  useDeleteCostMutation,
  useDeleteMultiCostsMutation,
} = costApi;
